import { IconButton, InputAdornment } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { Dispatch, SetStateAction, useState } from 'react'
import theme from 'lib/theme'
import { useTranslation } from 'react-i18next'
import { SmallDropdown } from 'components/SmallDropdown/SmallDropdown'
import { contentLanguages } from 'lib/languages'
import { SearchField, BlueSearchIcon } from './SearchConfig.styled'
import { ConfigFieldTypes } from './SearchConfig'

type SearchBarProps = {
  input: string
  setInput: (field: string, value: string) => void
  placeholderText: string
  handleClick: (values: ConfigFieldTypes) => void
  ariaLabel: string
  values: ConfigFieldTypes
  setPage: Dispatch<SetStateAction<number>>
}

export const SearchBar = ({
  input,
  setInput,
  placeholderText,
  handleClick,
  ariaLabel,
  values,
  setPage,
}: SearchBarProps) => {
  const { t } = useTranslation()
  const [focus, setFocus] = useState(false)
  const [hover, setHover] = useState(false)

  const clearSearch = () => {
    setInput('search', '')
    setFocus(false)
  }

  const handleSearch = () => {
    if (input && input.length !== 0) {
      handleClick(values)
      setPage(1)
      setFocus(true)
    } else {
      setFocus(false)
    }
  }

  return (
    <SearchField
      size='small'
      paddingLeft='0px'
      autoFocus={Boolean(input)}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onFocus={() => setFocus(true)}
      onBlur={() => setFocus(false)}
      hover={hover}
      value={input}
      id='outlined-basic'
      placeholder={placeholderText}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleSearch()
        }
      }}
      helperText={
        input && input.length < 3 && focus
          ? 'Enter at least three characters'
          : ' '
      }
      onChange={(e) => setInput('search', e.target.value)}
      InputProps={{
        ...(focus && {
          endAdornment: (
            <InputAdornment position='end'>
              {input && (
                <IconButton
                  onMouseDown={clearSearch}
                  aria-label={t('clearSearch')}
                  sx={{
                    width: '52px',
                    height: '46px',
                    '&:hover': {
                      background: 'transparent',
                      color: theme.palette.action.hover,
                    },
                  }}>
                  <CloseIcon
                    sx={{
                      width: '20px',
                      height: '20px',
                      '&:hover': {
                        cursor: 'pointer',
                        background: 'transparent',
                      },
                    }}
                  />
                </IconButton>
              )}
              <IconButton
                onMouseDown={handleSearch}
                aria-label={ariaLabel}
                sx={{
                  width: '52px',
                  height: '46px',
                  '&:hover': { background: 'transparent' },
                }}>
                <BlueSearchIcon query={input} sx={{ height: '1.8em' }} />
              </IconButton>
            </InputAdornment>
          ),
        }),
        startAdornment: (
          <InputAdornment position='start'>
            <SmallDropdown
              selectedOption={values.contentLanguage}
              options={contentLanguages}
              fieldName='contentLanguage'
              ariaLabel={t('selectLanguage')}
            />
          </InputAdornment>
        ),
      }}
    />
  )
}
